// Lane B3's screens, registered the way every lane registers its own (lane contract C-29, rebase R5) and hosted in
// MissionShell's content slot, inside its one `.mc` island (none of them draws a McRoot):
//
//   #candidates                         the Candidates table           gate candidate.search
//   #candidates/review/<parse_id>       Review parsed values           behind the parse's own read
//   #candidates/duplicates/<cm_id>      Duplicate review               behind the match's own read
//   #candidates/<cand_…>[/<tab>]        the candidate profile          behind candidate.profile
//   #candidates/ref/<ref>[/<tab>]       a rediscovered member, by the caller's own search ref
//   #pipeline[/R-####]                  the Pipeline board             gate candidate.list
//   #talent-community                   Talent community               gate candidate.search
//   #requisitions/R-####/batches/<n>    HM advance/hold/pass           the V1 route the HM's dated review task links to;
//                                                                     above B2's requisition detail (specificity 2)
//   #requisitions/R-####/candidates     the requisition's Candidates tab: this requisition's Pipeline board inside B2's
//                                       requisition detail (registerRequisitionTab, which registers the route); shown
//                                       to a seat that may call candidate.list; it replaces V1's Candidates link and
//                                       claims no deeper route
//   #requisitions/R-####/candidates/<application>
//                                       the requisition-scoped candidate page (UAT D-13): behind candidate.open's own
//                                       read; above B2's requisition detail (specificity 2) and never deeper, so V1's
//                                       round pages (…/rounds/<n>) keep theirs and B5's …/schedule its own
//
// Any other route under these views is the not-found screen, never a substitute record. The light nav row and the
// subjects are the server's: `app/candidate_items.py` registers the routes and the Action Center subjects and lights
// the Pipeline item (`shell_vocab.light_nav`); the Candidates item is lit by B1 and Talent community is lit by
// `app/tools/talent_community.py`, both behind candidate.search like their pages.
// App.tsx's former B3 cases and nav rows leave through the integration hunk in the lane report, with one import line
// in shell/views.ts. The screen cards (views/screen/RoundCards.tsx) mount under V1's round page and keep their McRoot.

import { createElement } from "react";
import { NAV_ITEM_LABELS } from "@/mc";
import { registerView, type ViewProps } from "@/shell/viewRegistry";
import { NotFound } from "@/views/NotFound";
import { ApplicationRoute } from "@/views/candidate/ApplicationPage";
import { CandidatesPage } from "@/views/candidate/CandidatesPage";
import { DuplicateReviewPage } from "@/views/candidate/DuplicateReviewPage";
import { ProfilePage } from "@/views/candidate/ProfilePage";
import { ReviewParsedPage } from "@/views/candidate/ReviewParsedPage";
import { TalentCommunityPage } from "@/views/candidate/TalentCommunityPage";
import { HmReviewPage } from "@/views/pipeline/HmReviewPage";
import { PipelineBoard, PipelinePage } from "@/views/pipeline/PipelinePage";
import { registerRequisitionTab } from "@/views/requisition/RequisitionDetail";
import type { RequisitionTabProps } from "@/views/requisition/slots";

const CAND = /^cand_[a-z0-9]+$/;
const RID = /^R-\d+$/;

function notFound(route: ViewProps["route"]) {
  return createElement(NotFound, { route: route.raw });
}

function CandidatesRoute({ route }: ViewProps) {
  const [first, second, third, ...rest] = route.parts;
  if (first === undefined) return createElement(CandidatesPage);
  if (rest.length) return notFound(route);
  if (first === "review" && second && third === undefined) return createElement(ReviewParsedPage, { key: second, parseId: second });
  if (first === "duplicates" && second && third === undefined) return createElement(DuplicateReviewPage, { key: second, matchId: second });
  if (first === "ref" && second) return createElement(ProfilePage, { key: `ref/${second}`, subject: { token: null, ref: second }, tab: third });
  if (CAND.test(first) && third === undefined) return createElement(ProfilePage, { key: first, subject: { token: first, ref: null }, tab: second });
  return notFound(route);
}

function PipelineRoute({ route }: ViewProps) {
  const [first, ...rest] = route.parts;
  if (first === undefined) return createElement(PipelinePage);
  if (rest.length || !RID.test(first)) return notFound(route);
  return createElement(PipelinePage, { key: first, rid: first });
}

function TalentCommunityRoute({ route }: ViewProps) {
  return route.parts.length ? notFound(route) : createElement(TalentCommunityPage);
}

function CandidatesTab({ r }: RequisitionTabProps) {
  return createElement(PipelineBoard, { key: r.id, rid: r.id });
}

function HmReviewRoute({ route }: ViewProps) {
  const [rid = "", , batch = ""] = route.parts;
  return createElement(HmReviewPage, { key: `${rid}/${batch}`, rid, batchNo: Number(batch) });
}

registerView("candidates", CandidatesRoute, { lane: "B3", gates: ["candidate.search"] });
registerView("pipeline", PipelineRoute, { lane: "B3", gates: ["candidate.list"] });
registerView("talent-community", TalentCommunityRoute, { lane: "B3", gates: ["candidate.search"] });
registerView(/^requisitions\/R-\d+\/batches\/\d+$/, HmReviewRoute, { lane: "B3", specificity: 2 });
registerView(/^requisitions\/R-\d+\/candidates\/[^/]+$/, ApplicationRoute, { lane: "B3", specificity: 2 });
registerRequisitionTab({
  key: "candidates",
  label: NAV_ITEM_LABELS.candidates,
  lane: "B3",
  order: 30,
  component: CandidatesTab,
  visible: (can) => can("candidate.list"),
});
